import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_PRICING_PLAN_TICK_RATES_RESOURCE } from 'src/features/pricingPlan/api/tickRate/useListPricingPlanTickRates';
import { useUpdatePricingPlanTickRate } from 'src/features/pricingPlan/api/tickRate/useUpdatePricingPlanTickRate';
import TickRateFormModal from 'src/features/pricingPlan/components/form/tickRate/TickRateFormModal';
import { PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface UpdatePricingPlanTickRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTickRate: PricingPlanTickRate;
    children?: React.ReactNode;
}

function UpdatePricingPlanTickRateFormModal<TData>({
    className,
    semantics,
    children,
    selectedTickRate,
}: UpdatePricingPlanTickRateFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const toastShown = React.useRef(false);
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdatePricingPlanTickRate({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_TICK_RATES_RESOURCE] });

            if (!toastShown.current) {
                toastShown.current = true;
                toast({
                    title: formatMessage(pricingPlanMessages.updateRateSuccess),
                    description: formatMessage(pricingPlanMessages.updateRateSuccessDescription),
                });
            }
        },
    });

    return (
        <TickRateFormModal
            selectedTickRate={selectedTickRate}
            className={className}
            showToast={toastShown}
            mutate={(values, onSuccess, onError) =>
                mutate(
                    { planId: selectedTickRate.pricing_plan, tickRateId: selectedTickRate.id, parkingId, ...values },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editRateSubtitle)}
        >
            {children}
        </TickRateFormModal>
    );
}

export default UpdatePricingPlanTickRateFormModal;
